import React, { useEffect, useRef, useState } from "react";
import { fileUrl, api } from "../lib/api";
import { X, Upload } from "lucide-react";

export function Scanlines() {
  return <div className="scanlines" aria-hidden />;
}

function resolveSrc(src) {
  if (!src) return "";
  if (src.startsWith("http") || src.startsWith("data:") || src.startsWith("blob:")) return src;
  return fileUrl(src);
}

export function Avatar({ src, name, size = 40, square = false, dim = false }) {
  const initials = (name || "?")
    .replace(/[^A-Za-zÀ-ÿ0-9\s]/g, "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
  const s = { width: size, height: size, minWidth: size };
  const radius = square ? 2 : "50%";
  if (src) {
    return (
      <img
        src={resolveSrc(src)}
        alt={name}
        style={{ ...s, borderRadius: radius, objectFit: "cover", opacity: dim ? 0.5 : 1 }}
        className="border border-[color:var(--r-primary)]/25"
      />
    );
  }
  return (
    <div
      style={{ ...s, borderRadius: radius, fontSize: size * 0.34 }}
      className="flex items-center justify-center font-mono font-bold border border-[color:var(--r-primary)]/30 bg-[color:var(--r-surface)] text-[color:var(--r-primary)]"
    >
      {initials || "?"}
    </div>
  );
}

export const PRIORITY = {
  NORMAL: { color: "#94A3B8", label: "NORMAL" },
  INFO: { color: "#2DD4BF", label: "INFO" },
  IMPORTANT: { color: "#38BDF8", label: "IMPORTANT" },
  ALERTE: { color: "#F59E0B", label: "ALERTE" },
  URGENT: { color: "#FB923C", label: "URGENT" },
  FLASH: { color: "#EF4444", label: "FLASH" },
};

export function PriorityBadge({ level }) {
  const p = PRIORITY[level] || PRIORITY.NORMAL;
  const pulse = ["ALERTE", "URGENT", "FLASH"].includes(level);
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 font-mono text-[10px] tracking-[0.2em] ${pulse ? "blink" : ""}`}
      style={{ color: p.color, border: `1px solid ${p.color}55`, background: `${p.color}12` }}
    >
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: p.color, boxShadow: `0 0 6px ${p.color}` }} />
      {p.label}
    </span>
  );
}

export function SignalBars({ className = "" }) {
  return (
    <div className={`flex items-end gap-0.5 ${className}`} aria-hidden>
      {[5, 8, 11, 14].map((h, i) => (
        <span key={i} style={{ width: 3, height: h, background: "var(--r-primary)", opacity: 0.5 + i * 0.12 }} />
      ))}
    </div>
  );
}

export function Modal({ open, onClose, title, children, width = 440 }) {
  useEffect(() => {
    function esc(e) { if (e.key === "Escape") onClose?.(); }
    if (open) document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      style={{ background: "rgba(3,5,8,0.78)", backdropFilter: "blur(4px)" }}
      onMouseDown={onClose}
    >
      <div
        className="frame w-full fade-up"
        style={{ maxWidth: width, maxHeight: "90vh", overflow: "auto", background: "var(--r-panel)" }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-[color:var(--r-primary)]/15">
          <span className="font-mono text-xs tracking-[0.2em] text-[color:var(--r-primary)]">{title}</span>
          <button data-testid="modal-close" onClick={onClose} className="text-[color:var(--r-text-muted)] hover:text-[color:var(--r-text)]">
            <X size={16} />
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

export function Field({ label, children }) {
  return (
    <label className="block mb-3">
      <span className="sys-label block mb-1.5">{label}</span>
      {children}
    </label>
  );
}

export function ReplaIdModal({ open, onClose, current, onSubmit }) {
  const [v, setV] = useState(current || "");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  React.useEffect(() => { setV(current || ""); setErr(""); }, [current, open]);
  async function save(custom) {
    setErr(""); setBusy(true);
    try { await onSubmit(custom ? v.trim().toUpperCase() : ""); onClose(); }
    catch (e) { setErr(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  }
  if (!open) return null;
  return (
    <Modal open={open} onClose={onClose} title="PERSONNALISER L'ID REPLA" width={420}>
      <Field label="ID (3 à 16 car. · lettres, chiffres, tirets)">
        <input data-testid="repla-id-input" className="input font-mono" value={v} onChange={(e) => setV(e.target.value.toUpperCase())} placeholder="EX : AGENT-NOX" autoFocus />
      </Field>
      <div className="sys-label mb-3" style={{ textTransform: "none", letterSpacing: "normal", color: "var(--r-text-muted)" }}>
        Pour un PJ avec un compte, cet ID est aussi son identifiant de connexion.
      </div>
      {err && <div className="font-mono text-xs mb-2 py-1.5 px-2" style={{ color: "var(--r-alert)", border: "1px solid var(--r-alert)" }}>✕ {err}</div>}
      <div className="flex gap-2">
        <button data-testid="repla-id-random" disabled={busy} className="btn btn-ghost flex-1" onClick={() => save(false)}>ALÉATOIRE</button>
        <button data-testid="repla-id-save" disabled={busy} className="btn btn-solid flex-1" onClick={() => save(true)}>ENREGISTRER</button>
      </div>
    </Modal>
  );
}

export function ConfirmModal({ open, onClose, onConfirm, title = "CONFIRMATION", message, confirmLabel = "CONFIRMER", danger = true }) {
  if (!open) return null;
  return (
    <Modal open={open} onClose={onClose} title={title} width={400}>
      <div className="text-sm mb-5" style={{ color: "var(--r-text)" }}>{message}</div>
      <div className="flex gap-2">
        <button data-testid="confirm-cancel" className="btn btn-ghost flex-1" onClick={onClose}>ANNULER</button>
        <button data-testid="confirm-action" className={`btn flex-1 ${danger ? "btn-danger" : "btn-solid"}`} onClick={onConfirm}>{confirmLabel}</button>
      </div>
    </Modal>
  );
}

export function AvatarUpload({ value, onChange, name = "", size = 56 }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  async function pick(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(r.data.path);
    } catch (err) {
      alert(err?.response?.data?.detail || "Échec de l'upload — réessayez");
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="flex items-center gap-3">
      <Avatar src={value} name={name} size={size} square />
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={pick} data-testid="avatar-upload-input" />
      <button type="button" data-testid="avatar-upload-button" onClick={() => ref.current?.click()} className="btn btn-ghost text-[10px] flex items-center gap-1">
        <Upload size={12} /> {busy ? "ENVOI…" : "UPLOADER UNE PHOTO"}
      </button>
      {value && <button type="button" onClick={() => onChange("")} className="text-[color:var(--r-text-muted)] hover:text-[color:var(--r-alert)]"><X size={14} /></button>}
    </div>
  );
}
